import { CaretDownOutlined, CaretRightOutlined } from "@ant-design/icons";
import { Button, Input, Modal, Select } from "antd";
import React, { useState } from "react";

import { GRID_DIFF_MODEL_OPTIONS } from "./types";

import type { SharePointItem } from "../types";

/** localStorage key used by both apps to persist the user's Claude key. */
export const COMPARE_API_KEY_STORAGE = "mj.docLib.claudeApiKey";

export interface CompareSetupValues {
  claudeApiKey: string;
  claudeModel: string;
  promptExtra: string;
}

export interface CompareSetupModalProps {
  open: boolean;
  files: SharePointItem[];
  onCancel: () => void;
  onStart: (values: CompareSetupValues) => void;
}

const DEFAULT_MODEL = "claude-sonnet-4-6";

/**
 * Pre-comparison modal. Collects model and optional extra prompt before
 * submitting the CAD-compare job.
 *
 * Claude API key is hardcoded in the backend for now — the key input UI is
 * commented out. Restore it (plus the useEffect / handleClearKey / apiKey
 * state) once the BFF exposes it as a caller-supplied param.
 */
export const CompareSetupModal: React.FC<CompareSetupModalProps> = ({
  open,
  files,
  onCancel,
  onStart
}) => {
  const [model, setModel] = useState<string>(DEFAULT_MODEL);
  const [promptExtra, setPromptExtra] = useState("");
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const handleStart = () => {
    onStart({
      claudeApiKey: "",
      claudeModel: model,
      promptExtra: promptExtra.trim()
    });
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title="Compare selected files"
      footer={null}
      width={520}
      centered
      destroyOnClose
    >
      <div className="space-y-4">
        <div className="bg-gray-50 rounded-lg p-3 space-y-1">
          <div className="flex items-baseline gap-2">
            <span className="text-xs font-medium text-gray-500 w-12 shrink-0">
              File A
            </span>
            <span className="text-sm text-gray-900 truncate">
              {files[0]?.name}
            </span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-xs font-medium text-gray-500 w-12 shrink-0">
              File B
            </span>
            <span className="text-sm text-gray-900 truncate">
              {files[1]?.name}
            </span>
          </div>
        </div>

        {/* Claude API key — restore once BFF exposes it as a caller-supplied param:
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <label htmlFor="compare-api-key" className="text-sm font-medium text-gray-700">
              Claude API key{" "}
              <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            {apiKey && (
              <button type="button" onClick={handleClearKey}
                className="text-xs text-gray-500 hover:text-gray-700 bg-transparent border-none cursor-pointer p-0">
                Clear
              </button>
            )}
          </div>
          <Input.Password id="compare-api-key" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
            placeholder="sk-ant-..." autoComplete="off" />
          <p className="text-xs text-gray-500 m-0">
            Without a key you&apos;ll see bounding boxes only. With a key, Claude generates a written description for each difference.
          </p>
        </div>
        */}

        <div>
          <button
            type="button"
            onClick={() => setAdvancedOpen((v) => !v)}
            className="flex items-center gap-1 text-sm text-gray-700 bg-transparent border-none cursor-pointer p-0"
          >
            {advancedOpen ? <CaretDownOutlined /> : <CaretRightOutlined />}
            Advanced
          </button>

          {advancedOpen && (
            <div className="mt-3 space-y-3">
              <div className="space-y-1">
                <label
                  htmlFor="compare-model"
                  className="text-sm font-medium text-gray-700"
                >
                  Model
                </label>
                <Select
                  id="compare-model"
                  value={model}
                  onChange={setModel}
                  options={[...GRID_DIFF_MODEL_OPTIONS]}
                  className="w-full"
                />
              </div>
              <div className="space-y-1">
                <label
                  htmlFor="compare-prompt"
                  className="text-sm font-medium text-gray-700"
                >
                  Additional prompt
                </label>
                <Input.TextArea
                  id="compare-prompt"
                  rows={3}
                  value={promptExtra}
                  onChange={(e) => setPromptExtra(e.target.value)}
                  placeholder="Extra context appended to the built-in prompt."
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="primary" onClick={handleStart}>
            Start Analysis
          </Button>
        </div>
      </div>
    </Modal>
  );
};
